import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import type { Pricing as PricingType } from '../types/content'
import { Download, Check } from './Icons'

interface PricingProps {
  data: PricingType
}

export default function Pricing({ data }: PricingProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="pricing" className="section" ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <h2 className="section-title text-gradient">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        {/* Pricing Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {data.plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`relative p-6 rounded-2xl border transition-all duration-300 hover:shadow-glow-lg ${
                plan.highlight
                  ? 'bg-gradient-to-br from-primary/20 to-secondary/10 border-primary/40 hover:border-primary/60'
                  : 'bg-gradient-card border-border hover:border-primary/30'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-6 px-3 py-1 text-xs font-medium rounded-full bg-primary text-dark">
                  {plan.badge}
                </span>
              )}

              <h3 className="text-xl font-semibold text-white mb-2">{plan.title}</h3>
              <p className="text-gray-400 text-sm mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className={`text-2xl font-bold ${plan.highlight ? 'text-primary' : 'text-white'}`}>
                  {plan.price}
                </span>
                {plan.unit && <span className="text-gray-400 text-sm ml-1">{plan.unit}</span>}
              </div>

              <ul className="space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Download Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <p className="text-gray-400 mb-6">{data.description}</p>

          <a
            href={data.downloadButton.file}
            download
            className="btn btn-primary inline-flex items-center gap-2 mb-4"
          >
            <Download className="w-5 h-5" />
            {data.downloadButton.text}
          </a>

          <p className="text-sm text-gray-500">{data.note}</p>
        </motion.div>
      </div>
    </section>
  )
}
